import React from 'react';
import { RangeFilter, RangeFilterEvents } from 'core/type/Filter.types';
import { useSelector } from 'react-redux';
import { Box, Checkbox, Rating, Typography } from '@drone-js/core';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { StringFormatter } from '@agoda/react-handyman';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesFilterStarRatingOptionClickEventActivitiesFieldsV6 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesFilterStarRatingOptionClickEventActivitiesManifestV6 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { FilterCode } from 'gql/autogen/FilterCode';

export interface Props extends RangeFilterEvents {
    title: string;
    items: RangeFilter[];
    elementName: string;
    isSidebar: boolean;
}

const StarRatingFilter: React.FC<Props> = React.memo((props) => {
    const cms = useSelector(selectCmsFromBootstrap);

    if (!props.items.length) {
        return null;
    }

    const onClick = (code: FilterCode, min: number, max: number, index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        trackElementEvent<ActivitiesFilterStarRatingOptionClickEventActivitiesFieldsV6>(
            ActivitiesFilterStarRatingOptionClickEventActivitiesManifestV6.ids,
            {
                is_sidebar: props.isSidebar,
            },
            { action_element_value: `${min}`, action_element_index: index + 1 },
        );
        props.onRangeFilterChange(code, { value: min, isInclusive: true }, { value: max, isInclusive: false }, event.target.checked);
    };

    return (
        <Box data-testid={`${props.elementName}-container`} pb="24">
            <Typography aria-label={props.title} data-testid={`${props.elementName}-title`} variant="heading-default" mt="0" mb="16">
                {props.title}
            </Typography>
            <div role="group" aria-label={props.title}>
                {props.items.map((item, key) => (
                    <Checkbox
                        selection={item.selected ? 'selected' : 'unselected'}
                        onChange={onClick(item.code, item.min, item.max, key)}
                        mb={key === props.items.length - 1 ? '0' : '8'}
                        key={key}
                        data-element-name={`${props.elementName}-option`}
                        data-testid={`${props.elementName}-option`}
                        data-element-index={key + 1}
                        label={item.label ? item.label : StringFormatter.format(cms[224212], item.min)}
                    >
                        {item.label ? (
                            <Typography variant="body-default">{item.label}</Typography>
                        ) : (
                            <Rating label={StringFormatter.format(cms[224212], item.min)} score={item.min} size="m" variant="star" color="hotel" />
                        )}
                    </Checkbox>
                ))}
            </div>
        </Box>
    );
});
StarRatingFilter.displayName = 'StarRatingFilter';

export default StarRatingFilter;
